// Critério único de "criativo 100% pronto" para agrupar cards em
// "Em processamento" vs "Prontos". O critério antigo (apenas analysis_status
// + optimizer_status) tirava o card da seção em processamento antes dos
// insights/mapas de atenção estarem prontos — o usuário clicava e via
// "Analisando Mídia… ETAPA 4 DE 4" no overlay. Esta regra exige que tudo
// que faz a ExperimentView renderizar análise esteja completado.
//
// Imagem: analysis + optimizer + insight + attention_map (original_media + todas prototypes)
// Vídeo:  analysis + insight + attention_map (original_media)

// 30/09 (gate all-or-nothing, specs/gate-all-or-nothing.md RN1/RN2): até aqui o
// conjunto terminal era {'completed', 'failed'} — 'failed' contava como pronto
// de propósito, porque quando um protótipo morria (workers de staging OOM'ando
// no meio do callback, protótipo abandonado com attention_map_status='failed')
// o card ficava preso em "Em processamento" PARA SEMPRE, sem erro e sem retry.
// O preço dessa escolha apareceu na conta FGV: um NeuroGen 3 só com a imagem
// gerada, sem heatmap e sem multiscore, servido como "Pronto". A regra nova é
// all-or-nothing: só 'completed' satisfaz, e falha declarada REPROVA o conjunto
// (ver hasAttentionMapFailure). O problema do card preso, que justificava a
// regra velha, não volta: a peça com falha declarada agora cai num TERCEIRO
// estado (isExperimentFailed), com a mensagem e o botão de refazer no card, em
// vez de ficar nem pronta nem falha.
const isAttentionMapCompleted = (media) =>
    media && media.attention_map_status === 'completed';

// Falha DECLARADA de mapa de atenção em qualquer mídia do conjunto (original ou
// protótipo). É a forma FGV: geração ok, callback do Forebrain perdido, watchdog
// carimba 'failed' meia hora depois — desfecho de falha, nunca prontidão.
const hasAttentionMapFailure = (item) =>
    item.original_media?.attention_map_status === 'failed'
    || (Array.isArray(item.prototypes)
        && item.prototypes.some((p) => p?.attention_map_status === 'failed'));

// 30/09: o backend passou a servir o desfecho do portão em `readiness`
// ('ready' | 'failed' | 'processing'), derivado em models.Experiment.readiness
// e presente no detalhe (ExperimentResponse), na listagem
// (ExperimentSummarySchema) e no broadcast de WS (`experiment_readiness`,
// carimbado no item pelo handler do Main.vue). Quando o campo vem, ele MANDA:
// este módulo é espelho declarado da régua do servidor, e espelho não desmente
// o original — a régua 'failed' do servidor cobre inclusive falhas que a
// listagem não expõe (carimbos de engajamento neural e de detecção de áreas).
// A régua local vira fallback para payload sem o campo: None/ausente significa
// "não calculado" (payload otimista, montado à mão, ou backend anterior ao
// campo durante o rollout), nunca um quarto estado.
const SERVER_READINESS = new Set(['ready', 'failed', 'processing']);
const serverReadiness = (item) =>
    (SERVER_READINESS.has(item?.readiness) ? item.readiness : null);

// DESFECHO POR MÍDIA (decisão de produto do Tomás, 25/09). "Pronto" deixou de ser
// "todo carimbo em completed" e passou a ser "todo sinal com DESFECHO": ou o sinal
// chegou, ou ele falhou DE VEZ. A régua nunca pode ficar esperando um sinal que
// não tem como terminar — esse é o beco sem saída que prendia o card em
// "Em processamento" para sempre, sem erro e sem retry.
//
// As duas funções abaixo são LEITURAS DEFENSIVAS de propósito: elas só bloqueiam
// quando o payload PROVA que sabe responder. O campo que dá essa prova está
// anotado em cada uma. Payload que não traz o campo se comporta exatamente como
// antes desta mudança — é o que permite o front ir para staging antes do backend
// que vai completar o `ExperimentSummarySchema`.

// DETECÇÃO DE ÁREAS. Dois desfechos: achou áreas, ou rodou e não achou nada
// (`aoi_autodetect_ran`, derivado no backend de `auto_aoi_snapshot is not None`).
// Mapa de atenção 'failed' não chega aqui desde 30/09: `isMediaSettled` exige
// `isAttentionMapCompleted` antes, e a falha reprova a peça em
// `hasAttentionMapFailure`. Vale também para a irmã abaixo.
//
// A PROVA é `aoi_autodetect_ran`: contar `aois` sozinho não serve, porque zero
// área é ambíguo entre "ainda detectando" e "detectou e não achou nada" (o mesmo
// motivo pelo qual o campo existe, ver MediaBase em schemas/assets.py). Quando
// ele não vem no payload, a régua não bloqueia por AOI.
// Sem guarda de nulo aqui e na irmã abaixo: `isMediaSettled` só as alcança depois
// de `isAttentionMapCompleted`, que já devolve falso para mídia ausente e
// curto-circuita o `&&`. Uma guarda a mais seria linha que nenhum caminho executa.
const isAoiTerminal = (media) => {
    if ((media.aois || []).length > 0) return true;
    if (media.aoi_autodetect_ran === true) return true;
    // Marcador de falha permanente da detecção. Ainda NÃO existe no backend: a
    // detecção que morre sem gravar o snapshot deixa `aoi_autodetect_ran` em
    // false para sempre. Lido aqui desde já para que, no dia em que o campo
    // chegar, o desfecho de falha entre sem tocar nesta régua de novo.
    if (media.aoi_autodetect_failed === true) return true;
    return media.aoi_autodetect_ran === undefined;
};

// ÍNDICE DE NEURAL ENGAGEMENT. Dois desfechos: o score existe, ou a perna do
// Brain AI morreu. O segundo é justamente o que a listagem NÃO sabia dizer (ver
// o comentário longo no fim deste arquivo): o marcador terminal é
// `Media.brainai_failed_at` e `MediaBase` não o expõe, então "o índice ainda vem"
// e "o índice nunca virá" chegam iguais — `neural_engagement_score: null` nos
// dois casos.
//
// A PROVA é `neural_engagement_failed`. Sem ele o payload não tem como fechar o
// sinal, e bloquear trancaria o card para sempre — pior do que o buraco que
// existe hoje (o gauge simplesmente some, `v-if="... !== null"`). Com ele, o
// índice entra no portão sem risco de tranca.
const isNeuralEngagementTerminal = (media) => {
    if (media.neural_engagement_score !== undefined && media.neural_engagement_score !== null) return true;
    if (media.neural_engagement_failed === true) return true;
    return media.neural_engagement_failed === undefined;
};

// Uma mídia da entrega está resolvida quando os TRÊS sinais que a tela consome
// dela (mapa de atenção, áreas, índice) têm desfecho.
// 30/09 (gate all-or-nothing): a perna do mapa deixou de ser `isAttentionMapTerminal`
// ('completed' OU 'failed') e passou a ser `isAttentionMapCompleted` (só
// 'completed'). As outras duas pernas ficam como estavam. É a juncao das duas
// decisoes: a de 25/09 continua exigindo DESFECHO dos tres sinais que a tela
// consome, e a de 29/09 (Billy) diz que falha declarada nao e desfecho que
// LIBERA, e sim que REPROVA. Sem esta linha o merge das duas deixava uma
// chamada a uma funcao que nao existe mais.
const isMediaSettled = (media) =>
    isAttentionMapCompleted(media) && isAoiTerminal(media) && isNeuralEngagementTerminal(media);

// A análise está ENTREGUE quando os insights existem, não quando o carimbo diz que
// sim. Os dois deveriam concordar: `analysis_status` e `insight_status` saem do mesmo
// PATCH de trigger_insights_generation. Só que depois disso o watchdog ainda
// sobrescreve APENAS o primeiro, porque a guarda de "já entregue" do handle-failure
// pergunta ao processing_job, que só fecha no fim do otimizador.
//
// Medição contra staging (15/09/2026): das 126 peças da bateria, TODAS com insights,
// 36 tinham `analysis_status='failed'` com optimizer completo, insights disponíveis e
// os 3 protótipos prontos. O cartão as marcava de vermelho e a página as escondia.
//
// `ai_insights_available` vem do backend como `bool(ai_insights) and insight_status ==
// 'completed'` (schemas/assets.py), logo é sinal de entrega, não um segundo carimbo.
// Falha de verdade não tem insight e continua sendo falha.
// Os dois sinais de entrega existem porque os dois payloads são diferentes:
// `ExperimentResponse` (detalhe, GET /experiments/{id}) traz `ai_insights_available`,
// derivado de `bool(ai_insights) and insight_status == 'completed'`;
// `ExperimentSummarySchema` (listagem, GET /folders) NÃO traz esse campo, só o
// `insight_status` cru. Ler apenas o primeiro é por que os cartões continuavam
// vermelhos depois do conserto da página: no item da listagem ele é sempre
// undefined, e a peça que abria perfeitamente seguia anunciada como falha.
export function isAnalysisDelivered(item) {
    if (!item) return false;
    if (item.analysis_status === 'completed') return true;
    // Só o carimbo de FALHA pode ser desmentido pela entrega. Peça em
    // 'processing'/'queued' é outra história: ela pode carregar insights de uma
    // rodada anterior (um retry re-executa com o texto velho ainda na linha), e
    // tratá-los como entrega desta rodada anunciaria "pronto" no meio do
    // reprocessamento. O defeito que estamos corrigindo é o watchdog carimbando
    // 'failed' por cima de trabalho pronto — é só esse caso que se desfaz aqui.
    if (item.analysis_status !== 'failed') return false;
    // O campo autoritativo é `ai_insights_available`, e ele só pode ser desempatado
    // pela AUSÊNCIA, nunca pelo valor. Um `||` entre os dois anularia o campo: o
    // backend calcula `bool(ai_insights) and insight_status == 'completed'`, logo
    // `ai_insights_available=false` COM `insight_status='completed'` é estado
    // produzível e significa insight vazio (a linha existe, o texto não). Tratar
    // isso como entrega tira o overlay de falha e entrega uma análise em branco.
    // `undefined` é outra coisa: é a listagem (ExperimentSummarySchema não traz o
    // campo), e aí o `insight_status` cru é o único sinal que chega.
    if (item.ai_insights_available !== undefined) return item.ai_insights_available === true;
    return item.insight_status === 'completed';
}

// Entrega completa: todas as etapas que a ExperimentView precisa para renderizar
// análise chegaram ao fim. Fica fora dos dois exports porque os DOIS precisam da
// mesma resposta e não podem chamar um ao outro: `isExperimentFullyReady` já
// consulta `isExperimentFailed` no caminho do latch, e fechar o ciclo daria
// recursão infinita.
function isDeliveryComplete(item) {
    if (!isAnalysisDelivered(item)) return false;
    if (item.insight_status !== 'completed') return false;
    if (!isAttentionMapCompleted(item.original_media)) return false;

    // Vídeo fica só com o mapa de atenção, como sempre: ele não passa pela
    // detecção de áreas de imagem (tem canal próprio, `video_aoi_status`) e não
    // tem protótipo. Exigir áreas aqui trancaria todo vídeo, que nunca vai
    // carimbar `aoi_autodetect_ran`.
    if (item.media_type === 'video') return true;

    // Imagem: o desfecho é de CADA mídia da entrega, original e NeuroGens. Era
    // isto que faltava — o mapa de atenção tinha desfecho, as áreas e o índice
    // não, e a tela abria com "Calculando..." por cima de entrega incompleta.
    if (!isMediaSettled(item.original_media)) return false;

    if (item.optimizer_status !== 'completed') return false;
    if (!Array.isArray(item.prototypes) || item.prototypes.length === 0) return false;
    return item.prototypes.every(isMediaSettled);
}

// PEÇA DE ANTES DA DETECÇÃO (09/10, boticario_01). Se nem o original passou
// pela detecção de áreas, nenhuma versão vai passar: é acervo de antes dela (87%
// das peças de imagem de produção). Não há o que esperar, e a versão sem área
// abre como "sem áreas". A peça em que o original detectou e um NeuroGen ficou
// sem é outra coisa: o servidor a reprova e oferece refazer
// (`Experiment.prototypes_missing_aoi_detection`).
export function isPreDetectionPiece(original) {
    return original?.aoi_autodetect_ran !== true;
}

// A VARIAÇÃO QUE NÃO PÔDE SER GERADA (Tomás, 25/09). Peça com falha permanente
// ABRE, degradada e com aviso — nunca em silêncio. O sinal é o mais simples que
// existe e que não mente: `attention_map_status === 'failed'` é o backend
// desistindo daquela mídia (OOM do worker, timeout do Forebrain, crash do
// ffmpeg), e é o mesmo estado que o portão trata como
// desfecho para deixar o card sair de "Em processamento". Aqui ele vira TEXTO:
// o que libera a tela é também o que precisa ser dito ao cliente, senão a
// entrega degradada se disfarça de entrega inteira.
export function isVariationUngenerated(media) {
    if (!media) return false;
    return media.attention_map_status === 'failed';
}

// Quantos NeuroGens da entrega ficaram pelo caminho. Zero para vídeo (não tem
// protótipo) e para qualquer coisa que não seja experimento.
export function countUngeneratedVariations(item) {
    if (!item || !Array.isArray(item.prototypes)) return 0;
    return item.prototypes.filter(isVariationUngenerated).length;
}

export function isExperimentFullyReady(item) {
    if (!item || item.type !== 'experiment') return false;
    // FALHA PRIMEIRO, inclusive por cima do latch e do servidor dizer 'ready'
    // num payload e 'failed' noutro: o latch é monotônico para o par
    // pronto/processando (SYN-1015), nunca um escudo contra falha declarada.
    // Sem esta ordem, a peça travada em latch sob a régua velha (30/09: a FGV
    // com o NeuroGen 3 morto) continuaria anunciada como "Pronta" depois de o
    // servidor já tê-la reprovado.
    if (isExperimentFailed(item)) return false;
    const fromServer = serverReadiness(item);
    if (fromServer === 'ready') return true;
    // Latch monotônico (SYN-1015): uma vez que o criativo foi observado 100%
    // pronto, ele permanece pronto — um rebuild atrasado do /folders (lag de
    // réplica, resposta em voo, debounce do refresh) não pode bouncear o card
    // de volta pra "Em andamento". Só uma falha genuína limpa o latch (checada
    // acima). O latch vence inclusive um `readiness: 'processing'` do servidor:
    // desde 30/09 a listagem carrega o campo, então o /folders defasado que o
    // latch existe para vencer chega com ele igualmente defasado.
    // buildItemTree carimba _readyLatched ao reconstruir uma árvore cujo item
    // anterior já estava pronto (ver stores/data.js). Sem isto o WS marca o
    // card pronto e o /folders defasado o joga de volta, oscilando ~10-15s.
    if (item._readyLatched) return true;
    if (fromServer === 'processing') return false;
    return isDeliveryComplete(item);
}

export function isExperimentFailed(item) {
    if (!item || item.type !== 'experiment') return false;
    // 30/09: quando o servidor mandou o desfecho, a resposta é dele — nas duas
    // direções. 'failed' reprova mesmo o que a régua local anistiaria, e
    // 'ready'/'processing' inocenta mesmo o que ela reprovaria: a régua do
    // servidor enxerga carimbos que a listagem não expõe e já embute a RN5
    // (insights em 'deferred' atrás do wizard aberto do cliente são espera,
    // nunca falha). O fallback abaixo só decide para payload sem o campo.
    const fromServer = serverReadiness(item);
    if (fromServer) return fromServer === 'failed';
    const s = item.analysis_status;
    // 'invalid' fica de fora da anistia de propósito: é recusa de política (duração
    // de vídeo acima do plano), o job nunca chegou a rodar, então não existe entrega
    // que possa inocentá-lo. O 'failed' é o que o watchdog carimba por cima de
    // trabalho pronto. A fase do otimizador segue com sinal próprio.
    if (s === 'invalid') return true;
    if (item.optimizer_status === 'failed') return true;
    // 30/09 (RN2): mapa de atenção 'failed' em qualquer mídia REPROVA, no
    // fallback como no servidor. Antes ele satisfazia o conjunto (era estado
    // "terminal"), e foi assim que a FGV recebeu um card "Pronto" com o
    // NeuroGen 3 só com a imagem gerada. A checagem vem antes do latch de
    // propósito: peça travada em latch sob a régua velha não pode seguir
    // anunciada como pronta com um protótipo morto dentro.
    if (hasAttentionMapFailure(item)) return true;
    if (s !== 'failed') return false;
    // Latch (SYN-1015): quem já foi visto 100% pronto não volta a falhar por causa de
    // um rebuild atrasado que trouxe uma etapa regredida. Para ele a anistia continua
    // sendo a entrega dos insights, como antes.
    if (item._readyLatched) return !isAnalysisDelivered(item);
    // Para os demais, a anistia exige a ENTREGA COMPLETA, não só os insights. A
    // anistia por insight sozinha criava um terceiro estado sem saída: o item saía de
    // "falhou" sem virar "pronto" (o otimizador ou um mapa de atenção nunca chegaram
    // ao fim), então o cartão ficava em "Em processamento" para sempre, sem erro e
    // sem botão de retry. E o carimbo do watchdog é justamente o sinal de que o
    // processing_job não fechou, ou seja, de que o downstream não vai mais avançar.
    return !isDeliveryComplete(item);
}

// Card vai pra "Em processamento" sempre que NÃO estiver 100% pronto e não
// tiver falhado. Cobre uploading_NN/pending/queued/validating/processing e
// também o gap entre analysis 'completed' e downstream stages (insights /
// attention_map / prototypes) terminarem.
export function isExperimentProcessing(item) {
    if (!item || item.type !== 'experiment') return false;
    if (isExperimentFailed(item)) return false;
    return !isExperimentFullyReady(item);
}

// O PORTÃO DE ABRIR (pedido do Tomás, 22/09): o clique no card só navega para a
// ExperimentView quando o carregamento INTEIRO terminou. Até 22/09 `selectItem`
// não perguntava nada sobre status — qualquer card abria, e a página renderizava
// esqueleto porque o gate dela (`isPipelineCompleted`) é mais frouxo que este:
// ele exige só analysis + optimizer, então insights e mapas de atenção ainda em
// curso viravam "Analisando Mídia… ETAPA 4 DE 4" por cima de uma tela vazia.
//
// A regra NÃO é nova: é exatamente a que já separa "Em processamento" de
// "Prontos" na grade (`isExperimentProcessing`) e a que o backend calcula em
// `Experiment.readiness` (models.py) para servir listagem, detalhe e broadcast.
// O defeito era o clique não consultá-la. Manter uma resposta só é o ponto: card
// na seção "Prontos" abre, card na seção "Em processamento" não — sem isso a
// UI prometeria com a seção o que negaria com o clique.
//
// FALHA DEIXOU DE ABRIR (30/09, gate all-or-nothing, RN3). A doutrina de 16/09
// ("falha sempre abre", porque a mensagem real e o retry moravam só na página)
// valia quando falha era sinônimo de analysis/optimizer mortos, caso que a
// página cobre com o overlay de erro. Sob a régua nova a falha típica é a forma
// FGV — análise completa e UM protótipo sem mapa —, e para ela a página só tem
// resultado parcial, exatamente o "esse pedaço não deu certo, mas olha o resto"
// que a RN3 proíbe. O desfecho não fica escondido: a tarja de falha, a mensagem
// amigável (error_message/validation_reason vêm na listagem desde SYN-2191) e o
// botão de refazer moram no próprio card (FileCard: isFailed + canRetry).
//
// 'abandoned' continua abrindo: não é falha (o carimbo não é 'failed' nem
// 'invalid'), nunca fica pronto, e o card não oferece ação para ele (só
// superadmin recupera) — barrar o clique seria o beco sem saída de volta.
//
// SOBRE OS ÍNDICES DOS NEUROGENS (o "e seus índices" do pedido). O índice de
// Neural Engagement de cada peça é `media.neural_engagement_score`, gravado num
// PATCH próprio que chega DEPOIS do `attention_map_status='completed'` da mesma
// mídia (ver activities.py, e o comentário em useExperimentData.js). Ele NÃO
// entra neste portão, por dois motivos apurados:
//
//   1. Para a mídia ORIGINAL ele já está coberto por transitividade: o backend
//      só dispara os insights quando `attention_map_status` E
//      `neural_engagement_status` estão 'completed' (activities.check_analysis_ready),
//      e o score é gravado ANTES do status na mesma activity. Logo
//      `insight_status === 'completed'`, que `isDeliveryComplete` já exige,
//      prova que o índice do original existe.
//   2. Para os PROTÓTIPOS (os NeuroGens) não existe escapatória visível daqui. A
//      perna BrainAI de um protótipo que morre não carimba status nenhum — o
//      marcador terminal é `Media.brainai_failed_at`, e `MediaBase`
//      (schemas/assets.py) NÃO o expõe, então a listagem não tem como distinguir
//      "o índice ainda vem" de "o índice nunca virá". Exigir o score aqui
//      trancaria o card para sempre nesse caso, que é justamente o estado que o
//      watchdog SYN-1449 existe para reconciliar. E a tela não mostra esqueleto
//      quando o score falta: o gauge é `v-if="... !== null"`, ou seja, some. É
//      entrega com buraco, não carregamento — prender seria esconder análise
//      entregue, o defeito de 16/09 de novo.
//
// Fechar (2) é mudança de backend: expor `brainai_failed_at` (ou um
// `neural_engagement_status` por mídia) em `MediaBase` daria o estado terminal
// que falta, e aí o score entra nesta regra sem risco de tranca.
//
// ATUALIZAÇÃO 25/09: o (2) deixou de ser "fora do portão" e virou "dentro, com
// leitura defensiva" (`isNeuralEngagementTerminal`). A decisão de produto passou
// a ser "cada sinal com desfecho", e o jeito de obedecer sem recriar a tranca é
// bloquear SÓ quando o payload prova que sabe responder: enquanto
// `neural_engagement_failed` não vier de `MediaBase`, a régua se comporta como
// descrito acima; quando vier, o índice do NeuroGen passa a ser exigido e o
// desfecho de falha o libera. O front funciona antes e depois, sem novo deploy.
// 30/09: na prática o backend fechou (2) por outra porta — a falha de
// engajamento neural reprova dentro de `Experiment.readiness`, que chega em
// `item.readiness` e manda nesta régua quando presente. No fallback local a
// limitação acima continua valendo.
export function isExperimentOpenable(item) {
    if (!item || item.type !== 'experiment') return false;
    if (isExperimentFailed(item)) return false;
    if (item.analysis_status === 'abandoned') return true;
    return isExperimentFullyReady(item);
}
